import { AreaConfig } from '#server/models';
import { ADMIN_SCROLLBAR_CLASSES } from '../../constants';
import { AdminPreview } from '../components/AdminPreview';
import { PreviewPanel } from '../components/PreviewPanel';
import { LabelEditor } from '../components/LabelEditor';
import { NumberField } from '../components/NumberField';
import { useLabelManager } from '../hooks/useLabelManager';

interface Step5Props {
  areaConfig: AreaConfig;
}

export const Step5Labels = ({ areaConfig }: Step5Props) => {
  const {
    draft,
    updateConfig,
    placingLabelId,
    setPlacingLabelId,
    expandedId,
    activeLabelId,
    pendingDeleteId,
    setPendingDeleteId,
    forceVisible,
    setForceVisible,
    labelRowRefs,
    onCameraControlsReady,
    addLabel,
    removeLabel,
    toggleExpanded,
    updateLabel,
    handleLabelClick,
    handleMeshClick,
    captureCamera,
  } = useLabelManager();

  const { config3D } = draft;

  return (
    <div className="flex gap-6 h-full">
      {/* Left: label list */}
      <div className="w-[380px] flex-shrink-0 flex flex-col max-h-[calc(100vh-280px)]">
        {/* Sticky header */}
        <div className="flex-shrink-0 space-y-2 pb-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-[#E8E8E8]">Step 5 – Labels</h2>
            <button
              onClick={addLabel}
              className="px-3 py-1 text-xs rounded bg-[#C3D82E] text-[#1A1A1A] font-semibold hover:bg-[#D4E845] transition-colors"
            >
              + Add Label
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 pb-2 border-b border-[#2A2A2A]">
            <NumberField
              label="Label Visible Dist"
              tooltip="Camera distance at which labels begin to appear. When the camera is further away than this value, all labels are hidden. Bring the camera closer and they start to fade in."
              value={config3D.labelVisibleDist}
              onChange={(v) => updateConfig({ labelVisibleDist: v })}
            />
            <NumberField
              label="Label Visible Range"
              tooltip="The distance over which labels fade in. A label becomes fully visible once the camera is closer than (Label Visible Dist − Label Visible Range). Larger values create a longer, softer fade; smaller values make labels appear more abruptly."
              value={config3D.labelVisibleRange}
              onChange={(v) => updateConfig({ labelVisibleRange: v })}
            />
          </div>
        </div>

        {/* Scrollable label list */}
        <div className={`flex-1 overflow-y-auto pr-2 space-y-1.5 ${ADMIN_SCROLLBAR_CLASSES}`}>
          {draft.labels.length === 0 && (
            <div className="text-sm text-[#666] space-y-1.5 py-2">
              <p className="text-[#888] font-medium">No labels yet</p>
              <p>Click <span className="text-[#C3D82E]">+ Add Label</span> to start. For each label:</p>
              <ol className="list-decimal list-inside space-y-0.5 text-xs pl-1">
                <li>Click the model in the preview to place it</li>
                <li>Orbit to the desired view, then capture the camera</li>
                <li>Fill in the title, categories, and units</li>
              </ol>
            </div>
          )}

          {draft.labels.map((label, index) => {
            const isExpanded = expandedId === label._id;
            const isPlacing = placingLabelId === label._id;
            return (
              <div
                key={label._id}
                ref={(el) => {
                  if (el) labelRowRefs.current.set(label._id, el);
                  else labelRowRefs.current.delete(label._id);
                }}
                className="rounded border border-[#2A2A2A] overflow-hidden"
              >
                {/* Accordion header */}
                <div
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none hover:bg-[#1F1F1F] transition-colors"
                  onClick={() => toggleExpanded(label._id)}
                >
                  <span className="text-xs text-[#888] w-5 shrink-0">#{index + 1}</span>
                  <span className="text-sm font-medium text-[#CCC] flex-1 truncate min-w-0">
                    {label.title || '(untitled)'}
                  </span>
                  {isPlacing && (
                    <span className="text-xs text-[#F59E0B] animate-pulse shrink-0">Placing…</span>
                  )}
                  {pendingDeleteId === label._id ? (
                    <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-[#F55]">Delete?</span>
                      <button
                        onClick={() => { removeLabel(label._id); setPendingDeleteId(null); }}
                        className="text-xs px-1.5 py-0.5 rounded bg-[#F55] text-white hover:bg-red-500 transition-colors"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setPendingDeleteId(null)}
                        className="text-xs px-1.5 py-0.5 rounded border border-[#444] text-[#AAA] hover:bg-[#333] transition-colors"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setPendingDeleteId(label._id); }}
                      className="text-xs text-[#555] hover:text-[#F55] transition-colors shrink-0 px-1"
                      aria-label="Remove label"
                    >
                      ✕
                    </button>
                  )}
                  <span className="text-[#555] text-xs shrink-0">{isExpanded ? '▾' : '▸'}</span>
                </div>

                {/* Expanded editor */}
                {isExpanded && (
                  <div className="border-t border-[#2A2A2A]">
                    <LabelEditor
                      label={label}
                      areaConfig={areaConfig}
                      onUpdate={(patch) => updateLabel(label._id, patch)}
                      onPlaceOnModel={() => setPlacingLabelId(label._id)}
                      onCaptureCamera={() => captureCamera(label._id)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: 3D preview */}
      <PreviewPanel
        className="flex-1"
        headerAction={
          <label className="flex items-center gap-1.5 text-xs text-[#999] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={forceVisible}
              onChange={(e) => setForceVisible(e.target.checked)}
              className="accent-[#C3D82E]"
            />
            Show all labels
          </label>
        }
      >
        <div className="relative h-full">
          <AdminPreview
            draft={draft}
            areaConfig={areaConfig}
            showLabels
            showAxes={false}
            activeLabelId={activeLabelId}
            onMeshClick={placingLabelId ? handleMeshClick : undefined}
            onLabelClick={handleLabelClick}
            onCameraControlsReady={onCameraControlsReady}
            placingLabelId={placingLabelId}
            forceVisible={forceVisible}
          />
          {placingLabelId && (
            <div className="absolute bottom-0 left-0 right-0 z-10 py-2 text-center text-sm font-medium text-[#1A1A1A] bg-[#C3D82E]/90 pointer-events-none select-none rounded-b">
              Click anywhere on the model to place the label
            </div>
          )}
        </div>
      </PreviewPanel>
    </div>
  );
};
